/**
 * Confetti for celebration moments, loaded on demand (canvas-confetti is never
 * in the startup bundle). Skipped entirely under prefers-reduced-motion or when
 * the tab is hidden, so a background unlock never fires into nothing.
 */

type ConfettiOptions = {
  origin?: { x: number; y: number }
  /** 'sides' = two angled bursts (arrival); 'fountain' = one upward burst (a button press). */
  shape?: 'sides' | 'fountain'
  /** CSS color values; resolved from theme tokens by the caller. */
  colors?: string[]
  particleCount?: number
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/** Resolve `--token` (an HSL triplet) to a hex string canvas-confetti accepts. */
export function tokenColor(token: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim()
  if (!raw) return fallback
  const probe = document.createElement('span')
  probe.style.color = `hsl(${raw})`
  document.body.appendChild(probe)
  const rgb = getComputedStyle(probe).color
  probe.remove()
  const match = rgb.match(/\d+(\.\d+)?/g)
  if (!match || match.length < 3) return fallback
  return `#${match
    .slice(0, 3)
    .map(n => Math.round(Number(n)).toString(16).padStart(2, '0'))
    .join('')}`
}

export async function burstConfetti(
  options: ConfettiOptions = {},
): Promise<void> {
  if (prefersReducedMotion()) return
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden')
    return
  try {
    const { default: confetti } = await import('canvas-confetti')
    const base = {
      particleCount: options.particleCount ?? 60,
      spread: 70,
      startVelocity: 38,
      ticks: 180,
      scalar: 0.9,
      origin: options.origin ?? { x: 0.5, y: 0.45 },
      colors: options.colors,
      disableForReducedMotion: true,
      zIndex: 10000,
    }
    if (options.shape === 'fountain') {
      confetti({ ...base, angle: 90, spread: 80, startVelocity: 32 })
      return
    }
    confetti({
      ...base,
      angle: 60,
      origin: { ...base.origin, x: base.origin.x - 0.12 },
    })
    confetti({
      ...base,
      angle: 120,
      origin: { ...base.origin, x: base.origin.x + 0.12 },
    })
  } catch {
    // Decorative only.
  }
}

/** Confetti colors for an unlock: brand yolk, the badge tier, flame, primary. */
export function celebrationPalette(
  tier: 'bronze' | 'silver' | 'gold',
): string[] {
  return [
    tokenColor('--sunny-yolk', '#ffd957'),
    tokenColor(`--badge-${tier}`, '#e0a82e'),
    tokenColor('--streak-flame', '#ec6b2d'),
    tokenColor('--primary', '#3b5bdb'),
  ]
}

/** Confetti origin (0–1 viewport fractions) at an element's top center. */
export function originOf(
  element: Element | null,
): { x: number; y: number } | undefined {
  if (!element || typeof window === 'undefined') return undefined
  const rect = element.getBoundingClientRect()
  return {
    x: (rect.left + rect.width / 2) / window.innerWidth,
    y: rect.top / window.innerHeight,
  }
}
